import { useEffect, useRef, useState } from "react";
import { GlobeMap, type GlobeMapHandle } from "./components/GlobeMap";
import { StatePanel } from "./components/StatePanel";
import { NavDropdown } from "./components/NavDropdown";
import { StateSearchBar } from "./components/StateSearchBar";
import { SuggestionsButton } from "./components/SuggestionsButton";
import { AttributionButton } from "./components/AttributionButton";
import { PricingView } from "./views/PricingView";
import { ServicesView } from "./views/ServicesView";
import { TrainingView } from "./views/TrainingView";
import type { LLCData, ViewKey } from "./types";
import type { EntityType } from "./canonical/model";
import type { CsrDataFile } from "./canonical/csr";
import { identityByLegacyKey } from "./canonical/jurisdictions";
import {
  DEFAULT_ENTITY_TYPE,
  resolveSelectedEntityType,
  selectableEntityTypesForJurisdiction,
} from "./canonical/selectability";

export function App() {
  const [view, setView] = useState<ViewKey>("globe");
  const [llcData, setLlcData] = useState<LLCData>({});
  const [selectedState, setSelectedState] = useState<string | null>(null);
  // The selected entity type persists while the CSR moves between states.
  const [entityType, setEntityType] = useState<EntityType>(DEFAULT_ENTITY_TYPE);
  const [csrData, setCsrData] = useState<CsrDataFile | undefined>(undefined);
  const mapHandleRef = useRef<GlobeMapHandle | null>(null);

  // Load LLC data once at startup.
  useEffect(() => {
    fetch("/data/llc-data.json")
      .then((r) => r.json())
      .then((d: LLCData) => setLlcData(d))
      .catch((err) => console.error("Failed to load llc-data.json:", err));
  }, []);

  // Load the CSR data file: per-jurisdiction canonical topic views plus, for
  // internal status only, the global migration-readiness summary. Any failure
  // leaves it undefined, which keeps Corporation disabled everywhere and every
  // state on the legacy fallback — both safe defaults. The entity selector is
  // permanent UI and does not depend on this fetch succeeding.
  useEffect(() => {
    fetch("/data/csr.json")
      .then((r) => (r.ok ? r.json() : null))
      .then((d: CsrDataFile | null) => {
        if (d) setCsrData(d);
      })
      .catch(() => {
        /* gate stays closed, every state stays on the legacy fallback */
      });
  }, []);

  function selectStateByName(name: string | null) {
    setSelectedState(name);
    if (name && mapHandleRef.current) {
      mapHandleRef.current.flyToState(name);
    }
  }

  const showGlobeChrome = view === "globe";
  const panelOpen = !!selectedState && view === "globe";
  const info = selectedState ? llcData[selectedState] : null;

  // Which entity types have a RECORD to open in THIS jurisdiction.
  //
  // Not which products are live here — that is content a reviewer reads after
  // opening the record, and gating the button on it hid authored records from
  // the person whose job is to check them.
  //
  // Recomputed from the currently selected jurisdiction on every render, so the
  // route taken to get here is irrelevant: a map click, a search result,
  // restored state and a selection carried over from the previous state all
  // arrive at the same answer. There is no separate code path to keep in sync,
  // and no global migration flag in the calculation.
  const jurisdictionId = selectedState ? identityByLegacyKey(selectedState)?.id : undefined;
  const viewsHere =
    jurisdictionId && csrData
      ? {
          llc: csrData.entities.llc?.jurisdictions[jurisdictionId],
          corp: csrData.entities.corp?.jurisdictions[jurisdictionId],
        }
      : undefined;
  const enabledEntityTypes = selectableEntityTypesForJurisdiction(viewsHere);

  // Coerced rather than trusted. Corporation now follows the reviewer between
  // migrated states, which is what makes reviewing fourteen records in a row
  // bearable — but it can never follow them into a jurisdiction with no record,
  // where there would be nothing to render.
  const activeEntityType = resolveSelectedEntityType(entityType, enabledEntityTypes);

  // Write the coercion back, so the remembered preference is the coerced one.
  // Without this the stored value would silently disagree with what is on
  // screen, and picking the visibly-active LLC button would be a no-op that left
  // `corp` stored — which would then reappear on the next state that has one.
  useEffect(() => {
    if (entityType !== activeEntityType) setEntityType(activeEntityType);
  }, [entityType, activeEntityType]);

  // The canonical topic view for this jurisdiction and the ACTIVE entity type,
  // if it has been migrated. Absent means the panel uses the legacy fallback.
  // Because the type is the coerced one, a disabled entity can never reach this
  // lookup, so no empty Corporation panel can render.
  const csrView = jurisdictionId ? viewsHere?.[activeEntityType] : undefined;

  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden" }}>
      {/* Map is always mounted; visibility toggles per view */}
      <GlobeMap
        visible={view === "globe"}
        selectedStateName={selectedState}
        onStateClick={(name) => selectStateByName(name)}
        onMapReady={(h) => {
          mapHandleRef.current = h;
        }}
      />

      {/* Non-globe overlays */}
      {view === "pricing" && (
        <div className="view-overlay">
          <PricingView />
        </div>
      )}
      {view === "services" && (
        <div className="view-overlay">
          <ServicesView />
        </div>
      )}
      {view === "training" && (
        <div className="view-overlay">
          <TrainingView />
        </div>
      )}

      {/* Top-left nav dropdown — always visible */}
      <NavDropdown active={view} onChange={setView} />

      {/* Globe-only chrome */}
      {showGlobeChrome && (
        <>
          <StateSearchBar
            data={llcData}
            onSelect={(name) => selectStateByName(name)}
          />
          <AttributionButton />
        </>
      )}

      {/* State panel only on globe view when a state is selected */}
      {panelOpen && info && selectedState && (
        <StatePanel
          stateName={selectedState}
          info={info}
          onClose={() => setSelectedState(null)}
          entityType={activeEntityType}
          onEntityTypeChange={setEntityType}
          enabledEntityTypes={enabledEntityTypes}
          {...(csrView ? { csrView } : {})}
        />
      )}

      {/* Suggestions button — visible across views */}
      <SuggestionsButton panelOpen={panelOpen} pagePath={`/${view}`} />
    </div>
  );
}
